import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Intervention, InterventionScopeNameEnum } from 'graphql-common';
import Typography from '@lib/components/Typography/Typography';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import {
  ORDERING_PARAMS,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import useInterventionsQueryHook from 'utils/fetch/hooks/useInterventionsQueryHook';
import getActiveInterventionsWidgetColumns from './utils/getActiveInterventionsWidgetColumns';
import NewBadge from '../../NewBadge';
import styles from './ActiveInterventionsWidget.module.scss';

export interface ActiveInterventionsWidgetProps {
  onlyMy?: boolean;
  showBadge?: boolean;
}

export default function ActiveInterventionsWidget({
  onlyMy = false,
  showBadge = false,
}: ActiveInterventionsWidgetProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const orderingState = useState<OrderingParams>(ORDERING_PARAMS);
  const paginationState = useState<PaginationParams>(PAGINATION_PARAMS);
  const [orderingParams] = orderingState;
  const [paginationParams] = paginationState;

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const {
    collection: interventions,
    totalCount,
    loading,
    error,
  } = useInterventionsQueryHook({
    fetchPolicy: 'cache-and-network',
    paginationParams,
    orderingParams,
    searchQuery,
    scopeName: onlyMy
      ? InterventionScopeNameEnum.OpenedMyParticipation
      : InterventionScopeNameEnum.Opened,
    includeFullSearchFilters: true,
  });

  const columns = useMemo(
    () => getActiveInterventionsWidgetColumns({ t }),
    [t],
  );

  const hasError = !!error;
  const widgetTitle = onlyMy
    ? t('my-active-interventions')
    : t('active-interventions');

  return (
    <div className={styles.container}>
      {showBadge && <NewBadge className={styles.badge} />}
      <div className={styles.header}>
        <Typography variant="h3" className={styles.title}>
          {widgetTitle}
        </Typography>
      </div>
      <div className={styles.searchWrapper}>
        <Search
          placeholder={t('interventions-search-placeholder')}
          onChange={handleSearchChange}
          useSearchParam={false}
          useBottomMargin={false}
        />
      </div>
      <div className={styles.tableWrapper}>
        {hasError ? (
          <EmptyState
            icon={IconName.Cancel}
            text={t('refresh-or-retry-later')}
            title={t('failed-to-load')}
          />
        ) : (
          <Table
            columns={columns}
            data={interventions as Intervention[]}
            id="active-interventions-widget"
            loading={loading}
            orderingState={orderingState}
            paginationState={paginationState}
            totalEntries={totalCount}
          />
        )}
      </div>
    </div>
  );
}
